import { MAX_FETCH_SIZE, ReturnInboundItem, ReturnInboundSearchParams } from '@/services/return/returnInbound/types'
import { MESSAGES } from '@/pages/return/returnInquiryAll/constants'

/** 뷰 조회영역 값(생성본 filterKey) — 화주유형·화주명은 '' 가 '전체' 다. */
export interface ViewFilter {
  service_type?: unknown
  shipper_name?: unknown
  PLN_DATE?: unknown
  ORD_NO?: unknown
  ORD_OPT_NO?: unknown
  ACT_DATE?: unknown
  ORD_TYPE?: unknown
  ORD_STATUS?: unknown
}

/** 다건 값 → 문자열 배열. '' 는 '전체'(조건 없음)라 버린다. */
export const asList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : typeof v === 'string' && v ? [v] : []
export const asText = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const asRange = (v: unknown): [string, string] | undefined =>
  Array.isArray(v) && typeof v[0] === 'string' && typeof v[1] === 'string' && v[0] && v[1] ? [v[0], v[1]] : undefined

/**
 * 조회 조건 = 서버 조건 + 화면에서 거르는 조건:
 * 입고유형 — BE 가 `ordType = RETURN` 고정이라 받지 않는다(moms-be#2410).
 */
export interface SearchCondition {
  params: ReturnInboundSearchParams
  orderType?: string
}

/**
 * 뷰 조회영역 값 → 조회 조건. 필수(화주·입고예정일자)가 비면 안내 문구를 돌려준다(옛 ReturnInquiryAllFilter.handleSearch 와 같은 검사).
 */
export const toSearchCondition = (f: ViewFilter): SearchCondition | { invalid: string } => {
  const types = asList(f.service_type)
  const names = asList(f.shipper_name)
  if (types.length === 0 && names.length === 0) return { invalid: MESSAGES.shipperRequired }
  const pln = asRange(f.PLN_DATE)
  if (!pln) return { invalid: MESSAGES.plnDateRequired }
  const act = asRange(f.ACT_DATE)
  return {
    params: {
      logisticsServiceTypes: types,
      shipperNames: names,
      plnDateFrom: pln[0],
      plnDateTo: pln[1],
      ordNumbers: asList(f.ORD_NO),
      ordOptionNumbers: asList(f.ORD_OPT_NO),
      orderStatus: asText(f.ORD_STATUS) || undefined,
      // 입고일자 — 1,000건 상한 안에서 화면이 거르면 결과가 비므로 서버가 거른다(LOGISPRD-14906). asRange 가 양끝을 다 요구해 한쪽만 보내는 400 은 없다.
      actDateFrom: act?.[0],
      actDateTo: act?.[1],
      page: 0,
      size: MAX_FETCH_SIZE,
    },
    orderType: asText(f.ORD_TYPE) || undefined,
  }
}

/** `LocalDateTime` 은 `2026-07-15T09:12:00` 로 온다 — 표에서는 초를 떼고 공백으로 잇는다(옛 columnDefinitions dateTime). */
const dateTime = (value: string | null) => (value ?? '').replace('T', ' ').slice(0, 16)

/**
 * BE 행 → 뷰 행(생성본 컬럼 dataIndex). key = BE 행 id.
 * 단위코드(ULD_TYPE)는 2.0 응답에 대응 필드가 없어 비워 둔다.
 * // TODO(api): ULD_TYPE(단위코드) — moms-be `GetAllReturnInboundsResponse` 에 필드 추가 필요(옛 화면도 이 칸을 뺐다).
 *   필드가 생기면 `ULD_TYPE: r.<필드> ?? null` 로 이 줄만 바꾼다(지금은 늘 null — 표에서 빈 칸).
 */
export const toViewRow = (r: ReturnInboundItem): Record<string, unknown> => ({
  key: String(r.id),
  service_type: r.serviceType,
  shipper_name: r.shipperName,
  SPR_NM: r.shipperCode,
  ORD_TYPE: r.orderType,
  BRD_NM: r.brandName,
  GDS_GROUP1: r.goodsGroup,
  GDS_CD: r.goodsCode,
  GDS_NM: r.goodsName,
  STL_NO: r.styleNumber,
  GDS_OPT: r.goodsOption,
  ORD_QTY: r.ordQty,
  BARCODE: r.barcode,
  SKU_ID: r.skuId,
  ORD_NO: r.ordNumber,
  ORD_OPT_NO: r.ordOptionNumber,
  ORD_STATUS: r.orderStatus,
  PLN_DATE: r.plnDate,
  ACT_DATE: r.actDate,
  ACT_QTY: r.actQty,
  ULD_TYPE: null,
  TRS_TYPE: r.trsType,
  TRS_TYPE_DETAIL: r.trsTypeDetail,
  TRS_LSP_NM: r.trsLspNm,
  DRV_NAME: r.drvName,
  TRS_NUMBER: r.trsNumber,
  TRS_DEPT_DT: r.trsDeptDt,
  TRS_ARRV_DT: r.trsArrvDt,
  REF_NO: r.refNo,
  REF_OPT_NO: r.refOptNo,
  PROC_NM: r.procNm,
  ORD_PROC_NM: r.ordProcNm,
  INS_DATE: dateTime(r.createdAt),
  INS_BY: r.createdBy,
  UPD_DATE: dateTime(r.updatedAt),
  UPD_BY: r.updatedBy,
  LOT_ATTR5: r.lotAttr5,
  TRS_INVOICE_NO: r.trsInvoiceNo,
  REF_NO2: r.refNo2,
})
